/**
 * @author: copilot
 * @date: 2026/3/29
 * @desc: Filter 组件演示
 */
import React, { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { Card } from 'tea-component';
import '@intelligence/v3/filterControls';
import Filter from '@intelligence/v3/components/Filter';
import useGenreSourcesOptions from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { APPMAGIC_TAG_TITLE_MAP } from '@intelligence/components/Filter/FilterBase/_config';
import { FILTER_TYPE } from '@intelligence/v3/filterControls/filterTypes';

import type { IFilterGroup, IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';

/**
 * 模拟异步获取 options 的 mock API
 */
const mockFetchOptions = (): Promise<{
  regionOptions: any[];
  platformOptions: any[];
  engineOptions: any[];
  sensorTowerTagOptions: Record<string, any[]>;
  sensorTowerThemeOptions: any[];
  appmagicTagOptions: Record<string, any[]>;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        regionOptions: [
          {
            area_code: 'east_asia',
            area_name_en: 'East Asia',
            area_name_ch: '东亚',
            country_set: [
              { country_code: 'CN', country_name_en: 'China', country_name_ch: '中国' },
              { country_code: 'JP', country_name_en: 'Japan', country_name_ch: '日本' },
              {
                country_code: 'KR',
                country_name_en: 'South Korea',
                country_name_ch: '韩国',
              },
            ],
          },
          {
            area_code: 'north_america',
            area_name_en: 'North America',
            area_name_ch: '北美',
            country_set: [
              {
                country_code: 'US',
                country_name_en: 'United States',
                country_name_ch: '美国',
              },
              {
                country_code: 'CA',
                country_name_en: 'Canada',
                country_name_ch: '加拿大',
              },
            ],
          },
          {
            area_code: 'europe',
            area_name_en: 'Europe',
            area_name_ch: '欧洲',
            country_set: [
              {
                country_code: 'GB',
                country_name_en: 'United Kingdom',
                country_name_ch: '英国',
              },
              {
                country_code: 'DE',
                country_name_en: 'Germany',
                country_name_ch: '德国',
              },
              {
                country_code: 'FR',
                country_name_en: 'France',
                country_name_ch: '法国',
              },
            ],
          },
        ],
        platformOptions: [
          { value: 'appstore', text: 'App Store' },
          { value: 'googleplay', text: 'Google Play' },
        ],
        engineOptions: [
          { value: 'unity', text: 'Unity' },
          { value: 'unreal', text: 'Unreal Engine' },
          { value: 'cocos', text: 'Cocos' },
        ],
        sensorTowerTagOptions: {
          sensor_tower_tag_art_style: [
            { value: 'Anime', text: 'Anime', textCN: '二次元' },
            { value: 'Pixel Graphics', text: 'Pixel Graphics', textCN: '像素风' },
            { value: 'Realistic', text: 'Realistic', textCN: '写实' },
            { value: 'Cartoon', text: 'Cartoon', textCN: '卡通' },
          ],
          sensor_tower_tag_camera_pov: [
            { value: 'Top-Down', text: 'Top-Down', textCN: '俯视角' },
            { value: 'Side View', text: 'Side View', textCN: '侧视角' },
            { value: 'Third Person', text: 'Third Person', textCN: '第三人称' },
          ],
          sensor_tower_tag_product_model: [
            { value: 'Single Player', text: 'Single Player', textCN: '单人' },
            { value: 'Multiplayer', text: 'Multiplayer', textCN: '多人' },
            { value: 'Live Ops', text: 'Live Ops', textCN: '长线运营' },
          ],
          sensor_tower_tag_setting: [
            { value: 'Fantasy', text: 'Fantasy', textCN: '奇幻' },
            { value: 'Post-apocalyptic', text: 'Post-apocalyptic', textCN: '后启示录' },
            { value: 'Modern City', text: 'Modern City', textCN: '现代都市' },
          ],
          sensor_tower_tag_tags: [
            { value: 'Gacha', text: 'Gacha', textCN: '抽卡' },
            { value: 'Idle', text: 'Idle', textCN: '放置' },
            { value: 'Match-3', text: 'Match-3', textCN: '三消' },
            { value: 'Tycoon', text: 'Tycoon', textCN: '模拟经营' },
          ],
          sensor_tower_tag_monetization: [
            { value: 'Battle Pass', text: 'Battle Pass', textCN: '战斗通行证' },
            { value: 'In-App Purchase', text: 'In-App Purchase', textCN: '应用内购' },
            { value: 'Ads', text: 'Ads', textCN: '广告变现' },
          ],
        },
        sensorTowerThemeOptions: [
          {
            value: 'RPG',
            text: 'RPG',
            textCN: '角色扮演',
            children: [
              { value: 'RPG@Fantasy', text: 'Fantasy', textCN: '奇幻' },
              { value: 'RPG@Dungeon', text: 'Dungeon', textCN: '地牢' },
              { value: 'RPG@Open World', text: 'Open World', textCN: '开放世界' },
            ],
          },
          {
            value: 'Strategy',
            text: 'Strategy',
            textCN: '策略',
            children: [
              { value: 'Strategy@4X', text: '4X', textCN: '4X' },
              { value: 'Strategy@War Simulation', text: 'War Simulation', textCN: '战争模拟' },
              { value: 'Strategy@Empire Builder', text: 'Empire Builder', textCN: '帝国经营' },
            ],
          },
          {
            value: 'Simulation',
            text: 'Simulation',
            textCN: '模拟',
            children: [
              { value: 'Simulation@Farm', text: 'Farm', textCN: '农场' },
              { value: 'Simulation@Life Sim', text: 'Life Sim', textCN: '生活模拟' },
              { value: 'Simulation@Restaurant', text: 'Restaurant', textCN: '餐厅经营' },
            ],
          },
        ],
        appmagicTagOptions: {
          appmagic_themes: [
            { value: 'Cozy', text: 'Cozy', textCN: '治愈' },
            { value: 'Fashion', text: 'Fashion', textCN: '时尚' },
            { value: 'Survival', text: 'Survival', textCN: '生存' },
          ],
          appmagic_features: [
            { value: 'Merge', text: 'Merge', textCN: '合成' },
            { value: 'Deck Building', text: 'Deck Building', textCN: '卡组构筑' },
            { value: 'Base Building', text: 'Base Building', textCN: '基地建设' },
          ],
          appmagic_ip: [
            { value: 'Anime', text: 'Anime', textCN: '动漫IP' },
            { value: 'Superhero', text: 'Superhero', textCN: '超级英雄' },
            { value: 'Sports', text: 'Sports', textCN: '体育IP' },
          ],
          appmagic_settings: [
            { value: 'Medieval', text: 'Medieval', textCN: '中世纪' },
            { value: 'Cyberpunk', text: 'Cyberpunk', textCN: '赛博朋克' },
            { value: 'School Life', text: 'School Life', textCN: '校园生活' },
          ],
          appmagic_art_styles: [
            { value: 'Low Poly', text: 'Low Poly', textCN: '低多边形' },
            { value: 'Chibi', text: 'Chibi', textCN: 'Q版' },
            { value: 'Hand-painted', text: 'Hand-painted', textCN: '手绘' },
          ],
        },
      });
    }, 1500);
  });
};

const MOCK_COMPANY_OPTIONS = ['Tencent', 'NetEase', 'miHoYo', 'Supercell', 'Lilith Games', 'FunPlus', 'Scopely'];
const DEMO_FILTER_GROUPS: IFilterGroup[] = [
  {
    key: 'basic',
    title: 'dbi_filter_group_popular',
    filterKeys: [
      'rangeTime',
      'myDate',
      'region',
      'genre',
      'allGames',
      'dataSource',
      'platForm',
      'allCompanys',
      'releaseTime',
    ],
  },
  {
    key: 'performance',
    title: 'dbi_filter_group_game_performance',
    filterKeys: ['activeUsersRange', 'revenueRange', 'arpuRange', 'downloadsRange', 'revenueGrowth'],
  },
  {
    key: 'sensor_tower',
    title: 'dbi_filter_group_sensor_tower',
    icon: getDataSourceIcon('sensortower'),
    filterKeys: [
      'sensor_tower_tag_art_style',
      'sensor_tower_tag_camera_pov',
      'sensor_tower_tag_product_model',
      'sensor_tower_tag_setting',
      'sensor_tower_tag_tags',
      'sensor_tower_tag_theme',
      'sensor_tower_tag_monetization',
    ],
  },
  {
    key: 'app_magic',
    title: 'dbi_filter_group_app_magic',
    icon: getDataSourceIcon('appmagic'),
    filterKeys: ['appmagic_themes', 'appmagic_features', 'appmagic_ip', 'appmagic_settings', 'appmagic_art_styles'],
  },
  {
    key: 'igdb',
    title: 'dbi_filter_group_igdb',
    icon: getDataSourceIcon('igdb'),
    filterKeys: ['game_engine'],
  },
];

/** 确认后的默认值（上层维护的 confirmed 值）*/
const DEFAULT_VALUE: IFilterValues = {
  rangeTime: {
    granularity: 'daily',
    startDate: '2026-01-01',
    endDate: '2026-01-18',
  },
  myDate: {
    granularity: 'monthly',
    startDate: '2026-01-01',
    endDate: '2026-03-31',
  },
  region: ['us', 'ca', 'fr'],
  genre: {
    source: 'sensortower',
    genres: [
      {
        value: 'Action',
        relation: Relation.Or,
        children: [{ value: 'Action Sandbox' }, { value: 'Artillery Shooter' }, { value: 'Brawl' }],
      },
      {
        value: 'Casino',
        relation: Relation.Or,
        children: [{ value: 'Coin Looters' }, { value: 'Dominoes' }],
      },
    ],
  },
  allGames: {
    value: [],
    keywords: 'clash',
    isSearchAll: false,
    checkedList: [],
  },
  dataSource: ['sensortower'],
  platForm: ['appstore', 'googleplay'],
  activeUsersRange: { min: 50000, max: 5000000 },
  revenueRange: { min: 20000, max: 1000000 },
  arpuRange: { min: 1, max: 50 },
  downloadsRange: { min: 100000, max: 10000000 },
  revenueGrowth: { opt: 'gte', value: '20' },
  allCompanys: ['Tencent', 'NetEase'],
  releaseTime: {
    granularity: 'daily',
    startDate: '2020-01-01',
    endDate: '2026-04-10',
  },
  sensor_tower_tag_art_style: ['Anime', 'Pixel Graphics'],
  sensor_tower_tag_camera_pov: ['Third Person'],
  sensor_tower_tag_product_model: ['Multiplayer', 'Live Ops'],
  sensor_tower_tag_setting: ['Fantasy'],
  sensor_tower_tag_tags: ['Gacha', 'Idle'],
  sensor_tower_tag_theme: ['RPG@Fantasy', 'Strategy@4X'],
  sensor_tower_tag_monetization: ['In-App Purchase', 'Battle Pass'],
  game_engine: { value: ['unity', 'unreal'], logic: 'OR' },
  appmagic_themes: ['Cozy', 'Survival'],
  appmagic_features: ['Merge'],
  appmagic_ip: ['Anime'],
  appmagic_settings: ['Medieval', 'Cyberpunk'],
  appmagic_art_styles: ['Low Poly', 'Hand-painted'],
};

const ConfirmedValuePanel: React.FC<{
  draftValue: Record<string, any>;
  value: Record<string, any>;
}> = (props) => {
  const { draftValue, value } = props;

  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        marginTop: 24,
      }}
    >
      <div
        style={{
          flex: 1,
          padding: 16,
          background: '#f5f7fa',
          borderRadius: 4,
          border: '1px solid #e5e5e5',
        }}
      >
        <div
          style={{
            marginBottom: 8,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Draft Value（编辑中的值）
        </div>
        <pre
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {JSON.stringify(draftValue, null, 2)}
        </pre>
      </div>

      <div
        style={{
          flex: 1,
          padding: 16,
          background: '#f5f7fa',
          borderRadius: 4,
          border: '1px solid #e5e5e5',
        }}
      >
        <div
          style={{
            marginBottom: 8,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Confirmed Value（已确认的值）
        </div>
        <pre
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    </div>
  );
};

/**
 * 完整场景 Demo（含 loading 效果演示：options 通过异步 mock API 获取）
 */
const FullFeatureDemo: React.FC = () => {
  const [confirmedValue, setConfirmedValue] = useState<IFilterValues>(DEFAULT_VALUE);
  const [draftValue, setDraftValue] = useState<IFilterValues>(DEFAULT_VALUE);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [mockOptions, setMockOptions] = useState<Record<string, any>>({});

  useEffect(() => {
    let isMounted = true;

    mockFetchOptions().then((data) => {
      if (!isMounted) {
        return;
      }

      setMockOptions(data);
      setOptionsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const { options: genreOptions } = useGenreSourcesOptions({
    entityType: 'mobile',
  });

  useEffect(() => {
    setDraftValue(confirmedValue);
  }, [confirmedValue]);

  const items = useMemo<IFilterItemConfig[]>(
    () => [
      {
        type: FILTER_TYPE.RANGE_PICKER,
        field: 'rangeTime',
        controlProps: {
          showSelectType: true,
          typeList: ['date', 'week', 'month'],
        },
      },
      {
        // 与 rangeTime 同为 RANGE_PICKER 类型，但 field 不同，演示同 type 不同 field 的用法
        type: FILTER_TYPE.RANGE_PICKER,
        field: 'myDate',
        controlProps: {
          showSelectType: true,
          typeList: ['date', 'week', 'month'],
        },
      },
      {
        type: FILTER_TYPE.REGION_SELECT,
        field: 'region',
        controlProps: {
          options: mockOptions.regionOptions || [],
          entityType: 'mobile',
        },
      },
      {
        type: FILTER_TYPE.GENRE_SELECT,
        field: 'genre',
        controlProps: {
          sources: ['annie', 'sensortower'],
          genresOptionsData: genreOptions,
        },
      },
      {
        type: FILTER_TYPE.GAME_SELECT,
        field: 'allGames',
        controlProps: {
          entityKey: 'app_id',
          isStudio: '',
          hasShortCut: false,
        },
      },
      {
        type: FILTER_TYPE.DATA_SOURCE,
        field: 'dataSource',
        controlProps: {},
      },
      {
        type: FILTER_TYPE.PLATFORM,
        field: 'platForm',
        controlProps: { options: mockOptions.platformOptions || [] },
      },
      {
        type: FILTER_TYPE.NUMBER_RANGE,
        field: 'activeUsersRange',
        label: 'dbi_active_users_range',
        controlProps: {},
      },
      {
        type: FILTER_TYPE.REVENUE_RANGE,
        field: 'revenueRange',
        label: 'dbi_revenue_range',
        controlProps: {
          unitText: '$',
        },
      },
      {
        type: FILTER_TYPE.REVENUE_RANGE,
        field: 'arpuRange',
        label: 'dbi_arpu_range',
        controlProps: {
          unitText: '$',
          clickSetDefault: true,
        },
      },
      {
        type: FILTER_TYPE.NUMBER_RANGE,
        field: 'downloadsRange',
        label: 'dbi_downloads_range',
        controlProps: {},
      },
      {
        type: FILTER_TYPE.REVENUE_GROWTH,
        field: 'revenueGrowth',
        label: 'dbi_revenue_growth',
        controlProps: {
          placeholderText: 'dbi_input_growth',
        },
      },
      {
        type: FILTER_TYPE.COMPANY_SELECT,
        field: 'allCompanys',
        controlProps: {
          entityType: 'mobile',
          source: 'sensortower',
          dataSources: ['ios', 'google_play'],
          metric: 'downloads',
          regionType: 'region',
          regions: ['global'],
          granularity: 'date',
          start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
          end_date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
          getCompanies: async (params: { search_name?: string }) => {
            const keywords = params?.search_name?.trim().toLowerCase() || '';
            const companyName = MOCK_COMPANY_OPTIONS.filter((item) => item.toLowerCase().includes(keywords)).map(
              (item) => ({ companyName: item }),
            );

            return Promise.resolve({
              data: {
                filter_flag: false,
                company_name: companyName,
              },
            });
          },
        },
      },
      {
        type: FILTER_TYPE.RANGE_PICKER,
        field: 'releaseTime',
        apiParamsFieldMapResolver: () => ({
          granularity: 'release_granularity',
          startDate: 'release_start_date',
          endDate: 'release_end_date',
        }),
        urlParamsFieldMapResolver: () => ({
          granularity: 'releaseGranularity',
          startDate: 'releaseStartDate',
          endDate: 'releaseEndDate',
        }),
        controlProps: {
          showSelectType: false,
          typeList: ['date'],
          showShortcut: false,
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'sensor_tower_tag_art_style',
        label: 'dbi_detail_art_style',
        controlProps: {
          options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_art_style || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'sensor_tower_tag_camera_pov',
        label: 'dbi_detail_camera_pov',
        controlProps: {
          options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_camera_pov || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'sensor_tower_tag_product_model',
        label: 'dbi_detail_product_model',
        controlProps: {
          options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_product_model || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'sensor_tower_tag_setting',
        label: 'dbi_detail_setting',
        controlProps: {
          options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_setting || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'sensor_tower_tag_tags',
        label: 'dbi_detail_tags',
        controlProps: {
          options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_tags || [],
        },
      },
      {
        type: FILTER_TYPE.SENSOR_TOWER_THEME,
        field: 'sensor_tower_tag_theme',
        label: 'dbi_detail_theme',
        controlProps: {
          options: mockOptions.sensorTowerThemeOptions || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'sensor_tower_tag_monetization',
        label: 'dbi_detail_monetization',
        controlProps: {
          options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_monetization || [],
        },
      },
      {
        type: FILTER_TYPE.GAME_ENGINE,
        field: 'game_engine',
        controlProps: {
          options: mockOptions.engineOptions || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'appmagic_themes',
        label: APPMAGIC_TAG_TITLE_MAP.appmagic_themes,
        controlProps: {
          options: mockOptions.appmagicTagOptions?.appmagic_themes || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'appmagic_features',
        label: APPMAGIC_TAG_TITLE_MAP.appmagic_features,
        controlProps: {
          options: mockOptions.appmagicTagOptions?.appmagic_features || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'appmagic_ip',
        label: APPMAGIC_TAG_TITLE_MAP.appmagic_ip,
        controlProps: {
          options: mockOptions.appmagicTagOptions?.appmagic_ip || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'appmagic_settings',
        label: APPMAGIC_TAG_TITLE_MAP.appmagic_settings,
        controlProps: {
          options: mockOptions.appmagicTagOptions?.appmagic_settings || [],
        },
      },
      {
        type: FILTER_TYPE.TAG_SELECT,
        field: 'appmagic_art_styles',
        label: APPMAGIC_TAG_TITLE_MAP.appmagic_art_styles,
        controlProps: {
          options: mockOptions.appmagicTagOptions?.appmagic_art_styles || [],
        },
      },
    ],
    [genreOptions, mockOptions],
  );

  return (
    <div>
      <Card>
        <Card.Body>
          <Filter
            loading={optionsLoading}
            items={items}
            value={confirmedValue}
            saveFilterType="filter-demo-test"
            useSticky
            stickyHeaderTitle="Filter Demo"
            onChange={(_field, _fieldValue, values) => {
              setDraftValue(values);
            }}
            onConfirm={(draftValues) => {
              setConfirmedValue(draftValues);
              console.debug('[Filter Demo] Confirmed:', draftValues);
            }}
            onReset={(draftValues) => {
              setDraftValue(draftValues);
              console.debug('[Filter Demo] Reset draft values:', draftValues);
            }}
            onFilterLoaded={(values) => {
              setDraftValue(values);
              console.debug('[Filter Demo] Loaded draft values:', values);
            }}
            useActionBar
            useSaveFilter
            filterGroups={DEMO_FILTER_GROUPS}
          />
        </Card.Body>
      </Card>
      <ConfirmedValuePanel draftValue={draftValue} value={confirmedValue} />
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const demoConfig = [
  {
    title: '完整筛选器场景',
    description:
      '展示全部筛选器类型（时间范围、地区、品类、游戏、数据源、平台、' +
      '数值范围、收入增长、公司、标签、引擎等）。' +
      '页面同时展示 draft 值与 confirmed 值，便于观察 Confirm/Reset/Load 的状态差异；' +
      '包含分组筛选器布局、Confirm/Reset 按钮、My Filter 下拉、More Filter 展开收起和滚动吸顶功能。' +
      '初始加载时通过异步 mock API 获取 options，展示 loading 骨架屏效果。',
    code: `<Filter
  loading={optionsLoading}
  items={items}
  value={confirmedValue}
  saveFilterType="filter-demo-test"
  filterGroups={DEMO_FILTER_GROUPS}
  useSticky
  stickyHeaderTitle="Filter Demo"
  onConfirm={(draftValues) => setConfirmedValue(draftValues)}
  onReset={(draftValues) => console.debug('[Filter Demo] Reset draft values:', draftValues)}
  useActionBar
  useSaveFilter
/>`,
    element: <FullFeatureDemo />,
  },
];

export default demoConfig;
